import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Stack,
  TextField,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Tooltip,
  Chip,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { Edit, Save, Warning, Info, Close } from "@mui/icons-material";
import useUpdateTimeParams from "../hooks/useUpdateTimeParams";
import useUpdateApplicationDates from "../hooks/useUpdateApplicationDates";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import QuestionnaireSummaryCard from "./QuestionnaireSummaryCard";
import { Autocomplete, createFilterOptions } from "@mui/material";
import useUnlinkedQuestionnaires from "../hooks/useUnlinkedQuestionnaires";
import useRelateQuestionnaires from "../hooks/useRelateQuestionnaires";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import { BANNER_COLOR_LIGHT, TEXT_COLOR } from "../pages/root";
import { useNavigate } from "react-router-dom";
import useToggleApplicationEnabled from "../hooks/useToggleApplicationEnabled";

const questionnaireFilterOptions = createFilterOptions({
  stringify: (option) => `${option.title ?? ""} ${option.external_id ?? ""}`,
});

const normalizeDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const formatDateCO = (value) => {
  const normalizedDate = normalizeDateInputValue(value);

  if (!normalizedDate) {
    return "Sin fecha";
  }

  const [year, month, day] = normalizedDate.split("-").map(Number);

  const date = new Date(Date.UTC(year, month - 1, day, 12));

  return new Intl.DateTimeFormat("es-CO", {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "America/Bogota",
  }).format(date);
};

const ApplicationSection = ({ app, isExpanded, refetchApplications }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [filteredQuestionnaires, setFilteredQuestionnaires] = useState(app.related_questionnaires || []);
  const [action, setAction] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [enabled, setEnabled] = useState(app.enabled !== false);
  const [pendingEnabledValue, setPendingEnabledValue] = useState(null);
  const [confirmEnabledOpen, setConfirmEnabledOpen] = useState(false);
  const { toggleApplicationEnabled, error: toggleEnabledError, loading: togglingEnabled } = useToggleApplicationEnabled();

  const {
    data: unlinkedQuestionnaires,
    loading: loadingUnlinked,
    error: unlinkedQuestionnairesError,
  } = useUnlinkedQuestionnaires(app.id, isExpanded);

  const [timed, setTimed] = useState(!!app.timed);

  const [selectedQuestionnaires, setSelectedQuestionnaires] = useState([]);
  const [errorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Estados para edición de fechas
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [updatingDates, setUpdatingDates] = useState(false);
  const [dateConfirmOpen, setDateConfirmOpen] = useState(false);
  const [dateParams, setDateParams] = useState({
    date_min: normalizeDateInputValue(app.date_min),
    date_max: normalizeDateInputValue(app.date_max),
  });

  const { relateQuestionnaires, loading: relating, error: relateError } = useRelateQuestionnaires();

  const { updateTimeParams, error } = useUpdateTimeParams();

  const { updateApplicationDates, error: updateDatesError } = useUpdateApplicationDates();

  // Convierte minutos totales a horas y minutos
  const toHoursAndMinutes = (totalMinutes) => ({
    hours: Math.floor(totalMinutes / 60),
    minutes: totalMinutes % 60,
  });

  const [timeParams, setTimeParams] = useState({
    warning: toHoursAndMinutes(app.time_warning_minutes || 120),
    closeWarning: toHoursAndMinutes(app.time_close_warning_minutes || 150),
    autoClose: toHoursAndMinutes(app.time_auto_close_minutes || 165),
  });

  //Valida que los minutos no superen los 59 minutos
  const handleChange = (key, field, value) => {
    let parsed = parseInt(value) || 0;

    if (field === "minutes") {
      parsed = Math.min(parsed, 59);
    }

    setTimeParams((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: parsed,
      },
    }));
  };

  // Maneja el cambio de las fechas de la aplicación
  const handleDateChange = (field, value) => {
    setDateParams((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Maneja la edición y validación de las fechas
  const handleDateButtonClick = () => {
    if (!isEditingDates) {
      setIsEditingDates(true);
      return;
    }

    if (!dateParams.date_min || !dateParams.date_max) {
      setErrorMessage("La fecha mínima y la fecha máxima son obligatorias.");
      setErrorOpen(true);
      return;
    }

    if (dateParams.date_min > dateParams.date_max) {
      setErrorMessage("La fecha mínima no puede ser mayor que la fecha máxima.");
      setErrorOpen(true);
      return;
    }

    setDateConfirmOpen(true);
  };

  // Cancela la edición y restaura las fechas originales
  const handleCancelDateEdit = () => {
    setDateParams({
      date_min: normalizeDateInputValue(app.date_min),
      date_max: normalizeDateInputValue(app.date_max),
    });

    setIsEditingDates(false);
  };

  // Maneja la confirmación de actualización de fechas
  const handleConfirmUpdateDates = async () => {
    setDateConfirmOpen(false);
    setUpdatingDates(true);

    const response = await updateApplicationDates({
      appId: app.id,
      date_min: dateParams.date_min,
      date_max: dateParams.date_max,
    });

    setUpdatingDates(false);

    if (!response?.error) {
      setIsEditingDates(false);
      refetchApplications();
    } else {
      setErrorMessage(response.message || "Error al actualizar las fechas de la aplicación.");
      setErrorOpen(true);
    }
  };

  // Maneja la confirmación de relación de encuestas
  const handleConfirmRelate = async () => {
    setConfirmOpen(false);

    const success = await relateQuestionnaires({
      appId: app.id,
      questionnaireIds: selectedQuestionnaires.map((q) => q.id),
    });

    if (success) {
      setSelectedQuestionnaires([]);
      refetchApplications();
    } else {
      setErrorMessage("No se pudieron relacionar los cuestionarios seleccionados");
      setErrorOpen(true);
    }
  };

  const handleTimedToggle = async (event) => {
    const newTimed = event.target.checked;
    const previousTimed = timed;

    setTimed(newTimed);

    const warningTotal = timeParams.warning.hours * 60 + timeParams.warning.minutes;

    const closeWarningTotal = timeParams.closeWarning.hours * 60 + timeParams.closeWarning.minutes;

    const autoCloseTotal = timeParams.autoClose.hours * 60 + timeParams.autoClose.minutes;

    setUpdating(true);

    const response = await updateTimeParams({
      appId: app.id,
      time_warning_minutes: warningTotal,
      time_close_warning_minutes: closeWarningTotal,
      time_auto_close_minutes: autoCloseTotal,
      timed: newTimed,
    });

    setUpdating(false);

    if (response?.error) {
      setTimed(previousTimed);

      setErrorMessage(response.message || "No se pudo actualizar el estado de cronometraje.");

      setErrorOpen(true);
    } else {
      refetchApplications();
    }
  };

  const handleQuestionnaireDelete = (questionnaireId, action = "delete") => {
    setFilteredQuestionnaires((prevQuestionnaires) => prevQuestionnaires.filter((q) => q.id !== questionnaireId));

    setAction(action);
    setShowSuccess(true);
  };

  const handleQuestionnaireError = (message) => {
    setErrorMessage(message);
    setErrorOpen(true);
  };

  const handleEnabledToggleClick = (_, newValue) => {
    const currentValue = enabled ? "enabled" : "disabled";

    if (!newValue || newValue === currentValue) return;

    setPendingEnabledValue(newValue === "enabled");
    setConfirmEnabledOpen(true);
  };

  const handleConfirmToggleEnabled = async () => {
    const success = await toggleApplicationEnabled({ appId: app.id, enabled: pendingEnabledValue });

    setConfirmEnabledOpen(false);

    if (success) {
      setEnabled(pendingEnabledValue);
      refetchApplications();
    } else {
      setErrorMessage("No se pudo actualizar el estado de habilitación de la aplicación.");
      setErrorOpen(true);
    }

    setPendingEnabledValue(null);
  };

  const handleCancelToggleEnabled = () => {
    setConfirmEnabledOpen(false);
    setPendingEnabledValue(null);
  };

  const handleButtonClick = async () => {
    if (!isEditing) {
      setIsEditing(true);
      return;
    }

    const warningTotal = timeParams.warning.hours * 60 + timeParams.warning.minutes;

    const closeWarningTotal = timeParams.closeWarning.hours * 60 + timeParams.closeWarning.minutes;

    const autoCloseTotal = timeParams.autoClose.hours * 60 + timeParams.autoClose.minutes;

    if (warningTotal === 0 || closeWarningTotal === 0 || autoCloseTotal === 0) {
      setErrorMessage("Ninguno de los parámetros de tiempo puede ser 0 minutos.");
      setErrorOpen(true);
      return;
    }

    setUpdating(true);

    // Construcción del objeto de datos para actualizar
    const response = await updateTimeParams({
      appId: app.id,
      time_warning_minutes: warningTotal,
      time_close_warning_minutes: closeWarningTotal,
      time_auto_close_minutes: autoCloseTotal,
    });

    setUpdating(false);

    if (!response?.error) {
      setIsEditing(false);
      refetchApplications();
    } else {
      setErrorMessage(response.message || "Error al actualizar los parámetros de tiempo.");
      setErrorOpen(true);
    }
  };

  useEffect(() => {
    setEnabled(app.enabled !== false);
  }, [app.enabled]);

  useEffect(() => {
    setTimed(!!app.timed);
  }, [app.timed]);

  useEffect(() => {
    setDateParams({
      date_min: normalizeDateInputValue(app.date_min),
      date_max: normalizeDateInputValue(app.date_max),
    });
  }, [app.date_min, app.date_max]);

  useEffect(() => {
    if (error || unlinkedQuestionnairesError || relateError || updateDatesError || toggleEnabledError) {
      const combinedError = error || unlinkedQuestionnairesError || relateError || updateDatesError || toggleEnabledError;
      setErrorMessage(combinedError?.message || "Error desconocido");
      setErrorOpen(true);
    }
  }, [error, unlinkedQuestionnairesError, relateError, updateDatesError, toggleEnabledError]);

  const TimeDisplay = ({ hours, minutes }) => (
    <Typography variant="body2" color="text.secondary">
      {hours}h {minutes}m
    </Typography>
  );

  const dateConfirmationDescription = [
    `¿Desea actualizar las fechas de la aplicación ${app.name}?`,
    "Las ventanas de las asignaciones asociadas también serán ajustadas.",
  ].join(" ");

  return (
    <>
      <Box
        sx={{
          mb: 3,
          p: 3,
          bgcolor: "background.paper",
          borderRadius: 2,
          borderLeft: "4px solid",
          borderColor: BANNER_COLOR_LIGHT,
          boxShadow: 1,
          transition: "all 0.3s ease",
          "&:hover": {
            boxShadow: 3,
          },
        }}
      >
        {/* Encabezado de la aplicación */}
        <Box
          sx={{
            mb: 2,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: TEXT_COLOR,
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <Typography variant="body2">
                <Box component="span" fontWeight={600}>
                  Aplicación ID:
                </Box>{" "}
                {app.id}
              </Typography>
            </Grid>

            {/* Botón de Ver asignaciones  y habilitar/deshabilitar*/}
            <Grid item xs={12} md={6} sx={{ display: "flex", justifyContent: { xs: "flex-start", md: "flex-end" }, alignItems: "center", gap: 2 }}>
              <ToggleButtonGroup
                value={enabled ? "enabled" : "disabled"}
                exclusive
                size="small"
                onChange={handleEnabledToggleClick}
                sx={{
                  borderRadius: "999px",
                  p: "3px",
                  bgcolor: "transparent",
                  border: "1px solid",
                  borderColor: "grey.300",

                  "& .MuiToggleButton-root": {
                    px: 2.5,
                    py: 0.5,
                    border: "none",
                    borderRadius: "999px !important",
                    textTransform: "none",
                    fontWeight: 500,
                    color: "text.secondary",
                    transition: "all 0.2s ease",

                    "&:not(.Mui-selected):hover": {
                      bgcolor: "rgba(0,0,0,0.04)",
                    },

                    "&.Mui-disabled": {
                      opacity: 0.5,
                    },
                  },
                }}
              >
                <ToggleButton
                  value="enabled"
                  disabled={togglingEnabled}
                  aria-label="habilitar aplicación"
                  sx={{
                    "&.Mui-selected": {
                      color: "black",
                      bgcolor: "primary.main",
                      boxShadow: 1,
                      "&:hover": {
                        bgcolor: "primary.light",
                      },
                    },
                  }}
                >
                  {togglingEnabled && enabled ? <CircularProgress size={18} color="inherit" /> : enabled ? "Habilitada" : "Habilitar"}
                </ToggleButton>

                <ToggleButton
                  value="disabled"
                  disabled={togglingEnabled}
                  aria-label="deshabilitar aplicación"
                  sx={{
                    "&.Mui-selected": {
                      color: "white",
                      bgcolor: "#e74040",
                      boxShadow: 1,
                      "&:hover": {
                        bgcolor: "#dd3a3a",
                      },
                    },
                  }}
                >
                  {togglingEnabled && !enabled ? <CircularProgress size={18} color="inherit" /> : !enabled ? "Deshabilitada" : "Deshabilitar"}
                </ToggleButton>
              </ToggleButtonGroup>

              <Button variant="contained" sx={{ textTransform: "none" }} onClick={() => navigate(`/aplicaciones/${app.id}/asignaciones`)}>
                Ver asignaciones
              </Button>
            </Grid>

            {/* Vigencia de la aplicación */}
            <Grid item xs={12}>
              <Box
                sx={{
                  mt: 1,
                  p: {
                    xs: 2,
                    md: 2.5,
                  },
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "grey.50",
                }}
              >
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  sx={{
                    mb: 2,
                  }}
                >
                  Fechas de la aplicación
                </Typography>

                {isEditingDates ? (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Fecha mínima"
                        type="date"
                        size="small"
                        fullWidth
                        value={dateParams.date_min}
                        disabled={updatingDates}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          max: dateParams.date_max || undefined,
                        }}
                        onChange={(event) => {
                          handleDateChange("date_min", event.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="Fecha máxima"
                        type="date"
                        size="small"
                        fullWidth
                        value={dateParams.date_max}
                        disabled={updatingDates}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        inputProps={{
                          min: dateParams.date_min || undefined,
                        }}
                        onChange={(event) => {
                          handleDateChange("date_max", event.target.value);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent={{
                          xs: "flex-start",
                          md: "flex-end",
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Close fontSize="small" />}
                          onClick={handleCancelDateEdit}
                          disabled={updatingDates}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Cancelar
                        </Button>

                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleDateButtonClick}
                          disabled={updatingDates}
                          startIcon={updatingDates ? <CircularProgress size={18} color="inherit" /> : <Save fontSize="small" />}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {updatingDates ? "Actualizando..." : "Actualizar fechas"}
                        </Button>
                      </Stack>
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" component="div">
                          Fecha mínima
                        </Typography>

                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            mt: 0.5,
                          }}
                        >
                          {formatDateCO(app.date_min)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: 1.5,
                          bgcolor: "background.paper",
                          border: "1px solid",
                          borderColor: "divider",
                          borderRadius: 1.5,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" component="div">
                          Fecha máxima
                        </Typography>

                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{
                            mt: 0.5,
                          }}
                        >
                          {formatDateCO(app.date_max)}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: {
                            xs: "flex-start",
                            md: "flex-end",
                          },
                        }}
                      >
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={handleDateButtonClick}
                          startIcon={<Edit fontSize="small" />}
                          sx={{
                            textTransform: "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Editar fechas
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                )}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-line",
                  fontWeight: 600,
                }}
              >
                Descripción:
              </Typography>

              <Box
                mt={1}
                p={2}
                sx={{
                  bgcolor: "#f5f5f5",
                  borderRadius: 2,
                  maxHeight: 300,
                  overflowY: "auto",
                  whiteSpace: "normal",
                }}
              >
                {app.description}
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Configuración de cronometraje */}
        <Box mb={2}>
          <FormControlLabel control={<Checkbox checked={timed} onChange={handleTimedToggle} disabled={updating} />} label="Cronometrada" />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: "block",
              mt: 0.5,
            }}
          >
            {timed
              ? "Los tiempos configurados se aplican " + "a las asignaciones de esta aplicación."
              : "Los parámetros de tiempo de la aplicación " + "no se aplican mientras la aplicación esté " + "marcada como no cronometrada."}
          </Typography>
        </Box>

        {/* Parámetros de tiempo */}
        <Box mb={2}>
          <Typography variant="subtitle2" fontWeight={300} mb={2}>
            Establecer tiempo para:
          </Typography>

          <Grid container spacing={3}>
            {[
              {
                label: "Advertencia tiempo considerable",
                key: "warning",
                icon: <Warning color="error" fontSize="small" />,
                tooltip: "Tiempo en el cual el cronómetro " + "de la asignación se torna de color rojo.",
              },
              {
                label: "Advertencia de cierre",
                key: "closeWarning",
                icon: <Warning color="error" fontSize="small" />,
                tooltip: "Momento en el que se le indicará al usuario " + "el tiempo restante antes del cierre " + "de la asignación.",
              },
              {
                label: "Cierre automático",
                key: "autoClose",
                icon: <Warning color="error" fontSize="small" />,
                tooltip: "Tiempo para cierre automático de una " + "asignación de esta aplicación.",
              },
            ].map(({ label, key, icon, tooltip }) => (
              <Grid item xs={12} md={4} key={key}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: timed ? "background.paper" : "#f5f5f5",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: !timed ? "grey.300" : isEditing ? "primary.light" : "divider",
                    transition: "all 0.2s ease",
                    opacity: timed ? 1 : 0.6,
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="flex-start"
                    mb={2}
                    sx={{
                      position: "relative",
                      width: "100%",
                    }}
                  >
                    {icon}

                    <Box
                      sx={{
                        flexGrow: 1,
                        pr: 3,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={500}>
                        {label}
                      </Typography>
                    </Box>

                    <Tooltip title={tooltip} arrow>
                      <Info
                        fontSize="small"
                        color="info"
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          cursor: "help",
                          "&:hover": {
                            color: "info.dark",
                          },
                        }}
                      />
                    </Tooltip>
                  </Stack>

                  {isEditing ? (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          label="Horas"
                          type="number"
                          size="small"
                          fullWidth
                          value={timeParams[key].hours}
                          disabled={!timed}
                          InputProps={{
                            inputProps: {
                              min: 0,
                            },
                            endAdornment: <InputAdornment position="end">h</InputAdornment>,
                          }}
                          onChange={(event) => {
                            handleChange(key, "hours", event.target.value);
                          }}
                        />
                      </Grid>

                      <Grid item xs={6}>
                        <TextField
                          label="Minutos"
                          type="number"
                          size="small"
                          fullWidth
                          value={timeParams[key].minutes}
                          disabled={!timed || !isEditing}
                          InputProps={{
                            inputProps: {
                              min: 0,
                              max: 59,
                            },
                            endAdornment: <InputAdornment position="end">m</InputAdornment>,
                          }}
                          onChange={(event) => {
                            handleChange(key, "minutes", event.target.value);
                          }}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <TimeDisplay {...timeParams[key]} />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Botón de acción */}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              variant={isEditing ? "contained" : "outlined"}
              onClick={handleButtonClick}
              disabled={updating || !timed}
              startIcon={
                updating ? <CircularProgress size={20} color="inherit" /> : isEditing ? <Save fontSize="small" /> : <Edit fontSize="small" />
              }
              sx={{
                minWidth: 200,
                borderRadius: 2,
                boxShadow: isEditing ? 2 : 0,
                "&:hover": {
                  boxShadow: isEditing ? 3 : 1,
                },
              }}
            >
              {isEditing ? (updating ? "Actualizando..." : "Actualizar parámetros de tiempo") : "Editar parámetros de tiempo"}
            </Button>
          </Box>
        </Box>

        {/* Cuestionarios relacionados */}
        <Box
          sx={{
            mt: 4,
          }}
        >
          <Typography variant="subtitle2" fontWeight={300} mb={1}>
            Relacionar nuevos cuestionarios:
          </Typography>

          <Autocomplete
            multiple
            options={unlinkedQuestionnaires || []}
            getOptionLabel={(option) => (option.external_id ? `${option.title} (${option.external_id})` : option.title)}
            filterOptions={questionnaireFilterOptions}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={loadingUnlinked}
            value={selectedQuestionnaires}
            onChange={(e, newValue) => {
              setSelectedQuestionnaires(newValue);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.title}
                {option.external_id && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    ({option.external_id})
                  </Typography>
                )}
              </li>
            )}
            renderTags={(value, getTagProps) => (
              <Box sx={{ maxHeight: 200, overflowY: "auto", display: "flex", flexWrap: "wrap" }}>
                {value.map((option, index) => {
                  const { key, ...tagProps } = getTagProps({ index });
                  return (
                    <Chip
                      key={option.id}
                      label={option.external_id ? `${option.title} (${option.external_id})` : option.title}
                      {...tagProps}
                      sx={{ maxWidth: "100%" }}
                    />
                  );
                })}
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Buscar y seleccionar cuestionarios"
                placeholder="Escriba para buscar por título o ID externo..."
              />
            )}
            sx={{ maxWidth: 600, mb: 2 }}
          />

          <Button
            variant="contained"
            disabled={selectedQuestionnaires.length === 0 || relating}
            onClick={() => {
              setConfirmOpen(true);
            }}
            startIcon={relating ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {relating ? "Relacionando..." : "Relacionar cuestionarios seleccionados"}
          </Button>
        </Box>

        <Box
          sx={{
            mt: 4,
          }}
        >
          <Typography variant="subtitle2" fontWeight={300} mb={2}>
            Cuestionarios relacionados a esta aplicación:
          </Typography>

          {filteredQuestionnaires?.length > 0 ? (
            <Grid container spacing={2}>
              {filteredQuestionnaires.map((questionnaireItem) => (
                <Grid item xs={12} sm={6} md={4} key={questionnaireItem.id}>
                  <QuestionnaireSummaryCard
                    questionnaireItem={questionnaireItem}
                    onDelete={handleQuestionnaireDelete}
                    onError={handleQuestionnaireError}
                    parent={{
                      type: "application",
                      id: app.id,
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" fontStyle="italic">
              No hay cuestionarios asociados a esta aplicación.
            </Typography>
          )}
        </Box>
      </Box>

      <SuccessSnackbar
        open={showSuccess}
        onClose={() => {
          setShowSuccess(false);
        }}
        message={`Cuestionario ${action == "unlink" ? "desvinculado" : "eliminado"} satisfactoriamente`}
      />

      <ErrorSnackbar
        open={errorOpen}
        onClose={() => {
          setErrorOpen(false);
        }}
        title="Ha ocurrido un error"
        message={errorMessage}
        icon={<Warning fontSize="large" />}
      />

      <ConfirmationModal
        open={confirmOpen}
        handleClose={() => {
          setConfirmOpen(false);
        }}
        handleConfirm={handleConfirmRelate}
        confirmText={relating ? "Relacionando..." : "Confirmar"}
        confirmColor="primary"
        description={`¿Desea relacionar los cuestionarios ` + `seleccionados a la aplicación "${app.name}"?`}
      />

      <ConfirmationModal
        open={dateConfirmOpen}
        handleClose={() => {
          setDateConfirmOpen(false);
        }}
        handleConfirm={handleConfirmUpdateDates}
        confirmText={updatingDates ? "Actualizando..." : "Confirmar"}
        confirmColor="primary"
        description={dateConfirmationDescription}
      />

      <ConfirmationModal
        open={confirmEnabledOpen}
        handleClose={handleCancelToggleEnabled}
        handleConfirm={handleConfirmToggleEnabled}
        title={pendingEnabledValue ? "Habilitar aplicación" : "Deshabilitar aplicación"}
        description={
          pendingEnabledValue
            ? `¿Desea habilitar la aplicación "${app.name}"? Los usuarios volverán a terner acceso a ella.`
            : `¿Desea deshabilitar la aplicación "${app.name}"? Los usuarios ya no podrán acceder a ella.`
        }
        confirmText={togglingEnabled ? "Actualizando..." : "Confirmar"}
        confirmColor={pendingEnabledValue ? "primary" : "error"}
      />
    </>
  );
};

export default ApplicationSection;
